import { PaymentElement } from '@stripe/react-stripe-js';

/**
 * MB WAY entry for its row (Figma: Checkout | Payment methods - mbway selected, node 1716:93024).
 *
 * **Deviates from the design, deliberately.** The frame draws a project-styled "Phone number*"
 * input, but the installed `@stripe/stripe-js` (9.12.1) has no `confirmMbWayPayment`, and
 * `ConfirmPaymentData.payment_method_data` carries no `type` field — so there is no typed way to
 * turn a phone number we collected ourselves into an `mb_way` PaymentMethod. `PaymentElement` is
 * the only supported path, and it renders its own phone field.
 *
 * The alternative — casting an untyped `payment_method_data: { type: 'mb_way' }` through
 * `confirmPayment` — was rejected: it relies on undocumented SDK behaviour on the one code path
 * where a silent break costs the customer money. The `appearance` options on the Elements group
 * bring Stripe's field close to the project's `Input`, but it will not be pixel-identical.
 *
 * Mounted in deferred-intent mode (`mode: 'payment'` on the Elements group), because the
 * PaymentIntent is only created once the customer presses pay.
 *
 * No label of our own: Stripe draws its own localised "Número de telefone" caption plus the
 * "authorise in the MB WAY app" note, and adding the design's "Phone number*" above it produced two
 * competing labels for one field.
 *
 * Two guards against the element being clipped, for two different causes:
 *  - `shrink-0` stops the surrounding flex column compressing the panel while the iframe is still
 *    reporting its height back to Stripe's script.
 *  - `[&_iframe]:min-h-50` (200px) floors the iframe itself. Stripe writes an inline `height` from
 *    its own measurement of the content, and any font-metric drift leaves it a few pixels short with
 *    the last instruction line cut in half. `min-height` clamps `height` per the CSS box model, so
 *    it wins over the inline style without `!important`. It has to be an absolute value: a
 *    percentage would resolve against this wrapper, whose height the iframe itself determines.
 *    200px clears the tallest observed state (pt-PT, two-line authorise note) with room to spare —
 *    over-reserving only adds blank space inside a transparent iframe, whereas under-reserving cuts
 *    text. The underlying cause, a webfont the iframe could not load, is fixed by the `fonts` option
 *    on the Elements group; this is the belt to those braces, since the content is third-party.
 */
interface MbWayPanelProps {
  /**
   * Which billing fields the caller will supply at confirm time.
   *
   * Opting a field out with `'never'` is a contract: Stripe then *requires* it in
   * `confirmParams.payment_method_data.billing_details` and throws an IntegrationError if it is
   * missing. So a field is only hidden when the profile actually has a value for it — otherwise it
   * stays `'auto'` and Stripe collects it, which is worse-looking than the design but never broken.
   */
  hideName: boolean;
  hideEmail: boolean;
}

export function MbWayPanel({ hideName, hideEmail }: MbWayPanelProps) {
  return (
    <div className="shrink-0 [&_iframe]:min-h-50">
      <PaymentElement
        options={{
          layout: 'tabs',
          // Only MB WAY is offered here — the row is the method choice, so Stripe must not present a
          // second, competing picker inside it.
          paymentMethodOrder: ['mb_way'],
          // `address` is left on `auto`: MB WAY does not need one, so Stripe renders nothing, and
          // opting out would oblige us to invent an address we do not have.
          fields: {
            billingDetails: {
              name: hideName ? 'never' : 'auto',
              email: hideEmail ? 'never' : 'auto',
            },
          },
        }}
      />
    </div>
  );
}
